import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  TextField,
  InputAdornment,
  Fab,
  Chip,
  Card,
  CardContent,
  CardActions,
  Grid,
  LinearProgress,
  Menu,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';

import {
  AddOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  WorkOutlineOutlined,
  LocationOnOutlined,
  PersonOutlined,
  MoreVertOutlined,
  CalendarTodayOutlined,
  TrendingUpOutlined,
  FilterListOutlined,
} from '@mui/icons-material';
import { useProjetStore } from '../stores/useProjetStore';
import { useClientStore } from '../stores/useClientStore';
import { StatutProjet } from '../types';
import { useNotification } from '../contexts/NotificationContext';

const ProjetsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { projets, loadProjets, deleteProjet, isLoading } = useProjetStore();
  const { clients, loadClients } = useClientStore();
  const { showConfirm, showSuccess } = useNotification();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatut, setSelectedStatut] = useState<StatutProjet | 'ALL'>('ALL');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProjetId, setSelectedProjetId] = useState<number | null>(null);

  useEffect(() => {
    loadProjets();
    if (clients.length === 0) {
      loadClients();
    }
  }, [loadProjets, loadClients, clients.length]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, projetId: number) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedProjetId(projetId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProjetId(null);
  };

  const getClientName = (clientId: number) => {
    const client = clients.find((c) => c.id === clientId);
    return client ? `${client.prenom} ${client.nom}` : 'Client inconnu';
  };

  const filteredProjets = projets.filter((projet) => {
    const search = searchQuery.toLowerCase();
    const clientName = getClientName(projet.clientId).toLowerCase();
    const matchesSearch =
      projet.titre.toLowerCase().includes(search) ||
      clientName.includes(search) ||
      projet.adresseChantier.toLowerCase().includes(search);

    const matchesStatut = selectedStatut === 'ALL' || projet.statut === selectedStatut;

    return matchesSearch && matchesStatut;
  });

  const handleDelete = async (id: number) => {
    const confirmed = await showConfirm({
      title: 'Supprimer le projet',
      message: 'Êtes-vous sûr de vouloir supprimer ce projet ? Tous les devis associés seront également supprimés.',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      severity: 'error',
    });

    if (confirmed) {
      try {
        await deleteProjet(id);
        showSuccess('Projet supprimé avec succès');
      } catch (error) {
        console.error('Erreur lors de la suppression du projet', error);
      }
    }
  };

  const getStatutColor = (statut: StatutProjet): string => {
    switch (statut) {
      case StatutProjet.EN_ATTENTE_DEVIS:
        return '#94A3B8'; // Gris slate
      case StatutProjet.DEVIS_ENVOYE:
        return '#3B82F6'; // Bleu
      case StatutProjet.DEVIS_ACCEPTE:
        return '#10B981'; // Vert
      case StatutProjet.EN_COURS:
        return '#F59E0B'; // Orange
      case StatutProjet.TERMINE:
        return '#059669'; // Vert foncé
      case StatutProjet.ANNULE:
        return '#EF4444'; // Rouge
      default:
        return '#94A3B8';
    }
  };

  const getStatutBgColor = (statut: StatutProjet): string => {
    switch (statut) {
      case StatutProjet.EN_ATTENTE_DEVIS:
        return '#F1F5F9';
      case StatutProjet.DEVIS_ENVOYE:
        return '#EFF6FF';
      case StatutProjet.DEVIS_ACCEPTE:
        return '#ECFDF5';
      case StatutProjet.EN_COURS:
        return '#FEF3C7';
      case StatutProjet.TERMINE:
        return '#D1FAE5';
      case StatutProjet.ANNULE:
        return '#FEE2E2';
      default:
        return '#F1F5F9';
    }
  };

  const getProgressValue = (statut: StatutProjet): number => {
    switch (statut) {
      case StatutProjet.EN_ATTENTE_DEVIS:
        return 10;
      case StatutProjet.DEVIS_ENVOYE:
        return 30;
      case StatutProjet.DEVIS_ACCEPTE:
        return 50;
      case StatutProjet.EN_COURS:
        return 75;
      case StatutProjet.TERMINE:
        return 100;
      case StatutProjet.ANNULE:
        return 0;
      default:
        return 0;
    }
  };

  const getStatutLabel = (statut: StatutProjet) => {
    switch (statut) {
      case StatutProjet.EN_ATTENTE_DEVIS:
        return 'En attente devis';
      case StatutProjet.DEVIS_ENVOYE:
        return 'Devis envoyé';
      case StatutProjet.DEVIS_ACCEPTE:
        return 'Devis accepté';
      case StatutProjet.EN_COURS:
        return 'En cours';
      case StatutProjet.TERMINE:
        return 'Terminé';
      case StatutProjet.ANNULE:
        return 'Annulé';
      default:
        return statut;
    }
  };

  // Statistiques par statut
  const stats = {
    total: projets.length,
    enAttente: projets.filter((p) => p.statut === StatutProjet.EN_ATTENTE_DEVIS).length,
    envoyes: projets.filter((p) => p.statut === StatutProjet.DEVIS_ENVOYE).length,
    acceptes: projets.filter((p) => p.statut === StatutProjet.DEVIS_ACCEPTE).length,
    enCours: projets.filter((p) => p.statut === StatutProjet.EN_COURS).length,
    termines: projets.filter((p) => p.statut === StatutProjet.TERMINE).length,
  };

  return (
    <Box>
      {/* Header avec titre et statistiques */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#333', display: 'flex', alignItems: 'center', gap: 1 }}>
            <WorkOutlineOutlined sx={{ fontSize: 32, color: '#F59E0B' }} />
            Mes Projets
          </Typography>
          <Chip
            label={`${stats.total} projet${stats.total > 1 ? 's' : ''}`}
            sx={{
              bgcolor: '#F59E0B',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.9rem',
              px: 1,
            }}
          />
        </Box>

        {/* Mini statistiques - Design épuré */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper
              elevation={selectedStatut === 'ALL' ? 2 : 0}
              sx={{
                p: 1.5,
                textAlign: 'center',
                bgcolor: 'white',
                borderLeft: '4px solid',
                borderLeftColor: selectedStatut === 'ALL' ? '#F59E0B' : '#E5E7EB',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                  borderLeftColor: '#F59E0B',
                },
              }}
              onClick={() => setSelectedStatut('ALL')}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#374151' }}>
                {stats.total}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Total
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper
              elevation={selectedStatut === StatutProjet.EN_COURS ? 2 : 0}
              sx={{
                p: 1.5,
                textAlign: 'center',
                bgcolor: 'white',
                borderLeft: '4px solid',
                borderLeftColor: selectedStatut === StatutProjet.EN_COURS ? '#F59E0B' : '#E5E7EB',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                  borderLeftColor: '#F59E0B',
                },
              }}
              onClick={() => setSelectedStatut(StatutProjet.EN_COURS)}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#F59E0B' }}>
                {stats.enCours}
              </Typography>
              <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 500 }}>
                En cours
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper
              elevation={selectedStatut === StatutProjet.DEVIS_ACCEPTE ? 2 : 0}
              sx={{
                p: 1.5,
                textAlign: 'center',
                bgcolor: 'white',
                borderLeft: '4px solid',
                borderLeftColor: selectedStatut === StatutProjet.DEVIS_ACCEPTE ? '#10B981' : '#E5E7EB',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                  borderLeftColor: '#10B981',
                },
              }}
              onClick={() => setSelectedStatut(StatutProjet.DEVIS_ACCEPTE)}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#10B981' }}>
                {stats.acceptes}
              </Typography>
              <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 500 }}>
                Acceptés
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper
              elevation={selectedStatut === StatutProjet.DEVIS_ENVOYE ? 2 : 0}
              sx={{
                p: 1.5,
                textAlign: 'center',
                bgcolor: 'white',
                borderLeft: '4px solid',
                borderLeftColor: selectedStatut === StatutProjet.DEVIS_ENVOYE ? '#3B82F6' : '#E5E7EB',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                  borderLeftColor: '#3B82F6',
                },
              }}
              onClick={() => setSelectedStatut(StatutProjet.DEVIS_ENVOYE)}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#3B82F6' }}>
                {stats.envoyes}
              </Typography>
              <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 500 }}>
                Envoyés
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper
              elevation={selectedStatut === StatutProjet.TERMINE ? 2 : 0}
              sx={{
                p: 1.5,
                textAlign: 'center',
                bgcolor: 'white',
                borderLeft: '4px solid',
                borderLeftColor: selectedStatut === StatutProjet.TERMINE ? '#059669' : '#E5E7EB',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                  borderLeftColor: '#059669',
                },
              }}
              onClick={() => setSelectedStatut(StatutProjet.TERMINE)}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#059669' }}>
                {stats.termines}
              </Typography>
              <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 500 }}>
                Terminés
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Barre de recherche */}
      <TextField
        fullWidth
        placeholder="Rechercher un projet par nom, client ou adresse..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            bgcolor: 'white',
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined sx={{ color: '#F59E0B' }} />
            </InputAdornment>
          ),
          sx: { fontSize: '1.1rem' },
        }}
      />

      {/* Grille de cartes projets */}
      {!isLoading && filteredProjets.length === 0 ? (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            bgcolor: 'white',
            borderRadius: 3,
            border: '2px dashed #E5E7EB',
          }}
        >
          <WorkOutlineOutlined sx={{ fontSize: 80, color: '#F59E0B', opacity: 0.3, mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            {searchQuery || selectedStatut !== 'ALL' ? 'Aucun projet trouvé' : 'Aucun projet enregistré'}
          </Typography>
          {!searchQuery && selectedStatut === 'ALL' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Créez votre premier projet pour organiser vos chantiers
              </Typography>
              <Fab
                variant="extended"
                color="primary"
                onClick={() => navigate('/projets/new')}
                sx={{
                  bgcolor: '#F59E0B',
                  '&:hover': { bgcolor: '#D97706' },
                }}
              >
                <AddOutlined sx={{ mr: 1 }} />
                Créer un projet
              </Fab>
            </>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredProjets.map((projet) => (
            <Grid item xs={12} sm={6} md={4} key={projet.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'visible',
                  borderTop: '4px solid',
                  borderTopColor: getStatutColor(projet.statut),
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                  },
                }}
                onClick={() => navigate(`/projets/${projet.id}`)}
              >
                {/* Badge statut en haut à droite */}
                <Chip
                  label={getStatutLabel(projet.statut)}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    bgcolor: getStatutBgColor(projet.statut),
                    color: getStatutColor(projet.statut),
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    zIndex: 1,
                  }}
                />

                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  {/* Titre du projet */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 1.5,
                      pr: 10,
                      color: '#1F2937',
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {projet.titre}
                  </Typography>

                  {/* Client */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PersonOutlined sx={{ fontSize: 18, color: '#6B7280' }} />
                    <Typography variant="body2" color="text.secondary">
                      {getClientName(projet.clientId)}
                    </Typography>
                  </Box>

                  {/* Adresse */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                    <LocationOnOutlined sx={{ fontSize: 18, color: '#6B7280', mt: 0.2 }} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: 1.4,
                      }}
                    >
                      {projet.adresseChantier}
                    </Typography>
                  </Box>

                  {/* Description (si présente) */}
                  {projet.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1.5,
                        fontStyle: 'italic',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {projet.description}
                    </Typography>
                  )}

                  {/* Dates */}
                  {(projet.dateDebut || projet.dateFin) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <CalendarTodayOutlined sx={{ fontSize: 16, color: '#6B7280' }} />
                      <Typography variant="caption" color="text.secondary">
                        {projet.dateDebut && new Date(projet.dateDebut).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                        {projet.dateDebut && projet.dateFin && ' → '}
                        {projet.dateFin && new Date(projet.dateFin).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
                      </Typography>
                    </Box>
                  )}

                  {/* Montant */}
                  {projet.montantTotal && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        bgcolor: '#F9FAFB',
                        borderRadius: 1,
                        mb: 1.5,
                      }}
                    >
                      <TrendingUpOutlined sx={{ fontSize: 18, color: '#10B981' }} />
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#10B981' }}>
                        {projet.montantTotal.toFixed(2)} €
                      </Typography>
                    </Box>
                  )}

                  {/* Barre de progression */}
                  <Box sx={{ mt: 'auto' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Progression
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: getStatutColor(projet.statut) }}>
                        {getProgressValue(projet.statut)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={getProgressValue(projet.statut)}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: '#E5E7EB',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: getStatutColor(projet.statut),
                          borderRadius: 3,
                        },
                      }}
                    />
                  </Box>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0, justifyContent: 'flex-end' }}>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, projet.id)}
                    sx={{
                      color: '#6B7280',
                      '&:hover': { bgcolor: '#F3F4F6' },
                    }}
                  >
                    <MoreVertOutlined />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Menu contextuel */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            if (selectedProjetId) {
              navigate(`/projets/${selectedProjetId}`);
              handleMenuClose();
            }
          }}
        >
          <WorkOutlineOutlined sx={{ mr: 1, fontSize: 20 }} />
          Voir les détails
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedProjetId) {
              navigate(`/projets/edit/${selectedProjetId}`);
              handleMenuClose();
            }
          }}
        >
          <EditOutlined sx={{ mr: 1, fontSize: 20 }} />
          Modifier
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedProjetId) {
              handleMenuClose();
              handleDelete(selectedProjetId);
            }
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteOutlined sx={{ mr: 1, fontSize: 20 }} />
          Supprimer
        </MenuItem>
      </Menu>

      {/* Bouton flottant pour ajouter */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 90,
          right: 20,
          bgcolor: '#F59E0B',
          width: 64,
          height: 64,
          boxShadow: '0 8px 24px rgba(245, 158, 11, 0.4)',
          '&:hover': {
            bgcolor: '#D97706',
            transform: 'scale(1.1)',
            boxShadow: '0 12px 32px rgba(245, 158, 11, 0.5)',
          },
          transition: 'all 0.3s ease',
        }}
        onClick={() => navigate('/projets/new')}
      >
        <AddOutlined sx={{ fontSize: 32 }} />
      </Fab>
    </Box>
  );
};

export default ProjetsListPage;
